import React from 'react';
import { Box, Button, Card, CardContent, Container, darken, Grid2, Typography } from '@mui/material';
import { Link } from 'react-router-dom';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import InstagramEmbed from '../../../components/InstagramEmbed';
import { useTranslation } from 'react-i18next';
import { ExpandableParagraph } from '../../../components/ExpandableParagraph';
import ToolShed from '../../../components/ToolShed';
import { PlanDetails, PlanAuthor, Tool, QuickOrderCost } from '../types';
import { InstructionVideoItem } from '../../../types';
import { CutsBlock } from './PlanViewInfoTabCutsBlock';
import useAuth from '../../../components/Auth';
import { PlanAuthorCaption } from '../PlanView';
import { PlanPrimaryInfoSection } from './PlanPrimaryInfoSection';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { AuthorBlock } from '../../../components/AuthorBlock';

interface PlanViewInfoTabInstagramContentProps {
    plan: PlanDetails;
    planAuthor: PlanAuthor;
    instructionsVideos: InstructionVideoItem[];
    companyTools: Tool[];
    quickOrderCosts: QuickOrderCost[];
    handleViewCutsClick: () => void;
    handleAddTool: (toolId: number) => void;
    handleRemoveTool: (toolId: number) => void;	
	onImageClick: (imageIndex: number) => void;
}

export const PlanViewInfoTabInstagramContent = ({plan, planAuthor, instructionsVideos, companyTools, quickOrderCosts, handleViewCutsClick, handleAddTool, handleRemoveTool}: PlanViewInfoTabInstagramContentProps) => {
	const theme = useTheme();
	const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
	const {userProfile} = useAuth();
	const companyToolsSet = new Set(companyTools.map((tool) => tool.id));
	const {t} = useTranslation();

	return <>
		<Grid2 container direction={isSmUp ? 'row' : 'column-reverse'}>
			<Grid2 size={{xs: 12, sm: 6}}>
				<Container disableGutters={!isSmUp}>
					<Card elevation={0}>
						<CardContent>
							{instructionsVideos.map((video) => (<Box key={video.id}>
								<InstagramEmbed videoId={video.instagramId} />
							</Box>))}
						</CardContent>
						{!plan.isVerifiedByAuthor && <Box display="flex" flexDirection="column" alignItems="center" gap={1} sx={{p: 2, backgroundColor: darken(theme.palette.background.paper, 0.05)}}>
							<PlanAuthorCaption authorName={plan.author.name || ''} source={plan.source} />
							<Typography variant='caption' color='text.secondary'>{t('planView.notAffiliatedWithAuthor')}</Typography>
						</Box>}
					</Card>
				</Container>
			</Grid2>
			<Grid2 size={{xs: 12, sm: 6}}>
				<Container>
					<PlanPrimaryInfoSection planId={plan.id} quickOrderCosts={quickOrderCosts} timeCost={plan.timeCost} ratingsSummary={plan.ratingsSummary} isBuyNowEnabled={Boolean(quickOrderCosts.length > 0)} tags={plan.tags} />
				</Container>
				{plan.instructions && <Box sx={{mt:2}}>
					<ExpandableParagraph text={plan.instructions} />
				</Box>}
			</Grid2>
		</Grid2>
		<Container>
			<Grid2 container mt={2}>
				{plan.cutsUrl && <Grid2 size={12}>
					<CutsBlock cutsUrl={plan.cutsUrl} onClick={handleViewCutsClick} />
				</Grid2>}
				{Boolean(plan.tools && plan.tools.length > 0) && <Grid2 size={{xs: 12, md: 8}} mt={5} offset={{xs: 0, md: 2}}>
					<ToolShed 
						title={t('planViewMaterialsCard.toolsUsed')}
						tools={plan.tools.map((tool) => tool.tool ? tool.tool : ({
							key: tool.name,
						}))} 
						companyToolsSet={userProfile ? companyToolsSet : undefined}
						onAddTool={userProfile ? handleAddTool : undefined}
						onRemoveTool={userProfile ? handleRemoveTool : undefined}
						sx={{mt: 4}}
					/>
				</Grid2>}
			</Grid2>
			<Box display='flex' justifyContent='center' sx={{mt: 5}}>
				<Button component={Link} variant='contained' to={`/plans/${plan.id}/supplies`} size='large' color='primary' endIcon={<ArrowForwardIcon />}>
					{t('planView.toSuppliesTab')}
				</Button>
			</Box>
			{plan.isVerifiedByAuthor && <Box mt={12}>
				<AuthorBlock authorInfo={planAuthor} />
			</Box>}
		</Container>
	</>;
};